// src/components/legal/NoweTechnologie.js

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Code, Shield, FileCode, Lock, Cpu, Scale, CheckCircle, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import Trust from './Trust';
import ContactForm from './ContactForm';

// Add font import
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const NoweTechnologie = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const services = [
    {
      icon: <FileCode className="w-8 h-8" />,
      title: "Prawo autorskie i własność intelektualna w IT",
      description: "Ochrona oprogramowania, baz danych, aplikacji."
    },
    {
      icon: <Code className="w-8 h-8" />,
      title: "Umowy IT",
      description: "Tworzenie i negocjowanie umów licencyjnych, wdrożeniowych, serwisowych, SaaS, outsourcingu IT."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "E-commerce",
      description: "Regulacje dotyczące sprzedaży online, praw konsumentów, polityki prywatności."
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Cyberbezpieczeństwo i RODO",
      description: "Doradztwo w zakresie zgodności z przepisami o ochronie danych osobowych i bezpieczeństwie informacji."
    },
    {
      icon: <Cpu className="w-8 h-8" />,
      title: "Nowe technologie",
      description: "Blockchain, sztuczna inteligencja, big data – analiza prawna i regulacyjna."
    },
    {
      icon: <Scale className="w-8 h-8" />,
      title: "Spory technologiczne",
      description: "Reprezentacja w sporach dotyczących IT, naruszenia praw własności intelektualnej."
    }
  ];

  const benefits = [
    "Głęboką wiedzę w szybko zmieniającej się dziedzinie",
    "Proaktywne podejście",
    "Zrozumienie specyfiki biznesów technologicznych",
    "Bezpieczeństwo prawne innowacyjnych projektów"
  ];

  const faqs = [
    {
      question: "Jakie umowy IT są kluczowe dla firm technologicznych?",
      answer: "Kluczowe są m.in. umowy wdrożeniowe, umowy serwisowe, licencyjne (SaaS, on-premise), umowy outsourcingowe, umowy o zachowaniu poufności (NDA) oraz umowy o dzieło/zlecenie z twórcami oprogramowania. Pomagamy w ich negocjowaniu i sporządzaniu."
    },
    {
      question: "Czy moja firma potrzebuje audytu RODO?",
      answer: "Każda firma przetwarzająca dane osobowe powinna regularnie przeprowadzać audyt zgodności z RODO, aby zidentyfikować ryzyka i upewnić się, że spełnia wymogi prawne. Jest to szczególnie ważne w sektorze nowych technologii."
    },
    {
      question: "Jak chronić własność intelektualną w branży IT?",
      answer: "Ochrona własności intelektualnej w IT obejmuje m.in. rejestrację znaków towarowych (dla nazw, logo), ochronę praw autorskich do kodu źródłowego i interfejsów, zabezpieczanie tajemnicy przedsiębiorstwa (know-how) oraz odpowiednie umowy z pracownikami i kontrahentami."
    }
  ];

  const handleContactScroll = () => {
    const contactSection = document.querySelector('#kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 animate-float">
            <Cpu className="w-32 h-32 text-violet-600" />
          </div>
          <div className="absolute bottom-20 left-20 animate-float animation-delay-2000">
            <Code className="w-24 h-24 text-violet-500" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Prawo Nowych Technologii i Innowacji
            </h1>
            <p className="text-2xl lg:text-3xl text-white mb-12 font-semibold">
              Dynamiczny rozwój technologii wymaga adekwatnego wsparcia prawnego.                Szynkowski Legal oferuje kompleksowe doradztwo w obszarze prawa nowych technologii i innowacji, chroniąc Twój biznes w cyfrowym świecie.

            </p>
            
        
            <div className="flex justify-center">
              <button 
                onClick={handleContactScroll}
                className="group flex items-center gap-3 text-white text-xl lg:text-2xl font-medium hover:text-violet-500 transition-all duration-300 uppercase"
              >
                <span>Bezpłatna konsultacja</span>
                <ArrowRight className="w-6 h-6 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-6 md:mb-8 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Doradztwo prawne dla branży technologicznej i innowacyjnych projektów
            </h2>
            <div className="space-y-6 text-lg text-gray-300">
              <p>
                W obliczu błyskawicznych zmian technologicznych, zrozumienie i przestrzeganie przepisów prawnych jest kluczowe dla bezpieczeństwa i sukcesu każdego innowacyjnego przedsięwzięcia.
              </p>
              <p>
                Nasz zespół ekspertów specjalizuje się w prawie nowych technologii, oferując wsparcie od startupów po dojrzałe firmy technologiczne.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Zakres naszych usług
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group backdrop-blur-md bg-white/5 rounded-xl p-8 border border-white/10 hover:border-violet-600/50 transition-all duration-300 hover:shadow-xl hover:shadow-violet-600/10"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <span className="text-violet-600">
                    {service.icon}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-400">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-600/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Dlaczego warto wybrać Szynkowski Legal?
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-violet-600 flex-shrink-0 mt-1" />
                  <span className="text-lg text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-xl text-gray-300 mb-8">
                Zapewnij sobie bezpieczny rozwój w świecie technologii. Skontaktuj się z nami po profesjonalne doradztwo prawne!
              </p>
              <button 
                onClick={handleContactScroll}
                className="group flex items-center gap-3 text-white text-xl lg:text-2xl font-medium hover:text-violet-500 transition-all duration-300 uppercase mx-auto"
              >
                <span>Bezpłatna konsultacja</span>
                <ArrowRight className="w-6 h-6 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl lg:text-5xl font-black text-white mb-12 text-center uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
            FAQ
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="backdrop-blur-md bg-white/5 rounded-xl border border-white/10 overflow-hidden transition-all duration-300 hover:border-violet-600/30"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between group"
                >
                  <h3 className="text-lg font-semibold text-white pr-4">
                    {faq.question}
                  </h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-violet-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-violet-600 transition-colors flex-shrink-0" />
                  )}
                </button>
                
                <div className={`transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'} overflow-hidden`}>
                  <div className="px-6 pb-4">
                    <p className="text-gray-400">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <Trust />

      {/* Contact Form */}
      <ContactForm />

      {/* Animation styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(10deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default NoweTechnologie;